import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  useTheme,
  CircularProgress,
} from '@mui/material'
import { Close as CloseIcon, Save as SaveIcon, Cancel as CancelIcon } from '@mui/icons-material'
import FileViewer from 'molecules/FileViewer/FileViewer'
import { useUpdateTranslationMapSetting } from '../../api'
import { toast } from 'atoms/Toast'

export interface SpecificationEditorDialogProps {
  open: boolean
  onClose: () => void
  onSave: (content: string) => void
  title: string
  content: string
  contentType?: 'md' | 'json' | 'yaml' | 'xml'
  disabled?: boolean
  translationMap?: any
  field: 'summary' | 'data_mapping'
  currentSpecification?: any
}

const SpecificationEditorDialog: React.FC<SpecificationEditorDialogProps> = ({
  open,
  onClose,
  onSave,
  title,
  content,
  contentType = 'md',
  disabled = false,
  translationMap,
  field,
  currentSpecification,
}) => {
  const theme = useTheme()
  const [localContent, setLocalContent] = useState(content)
  const [hasChanges, setHasChanges] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  // API hook for updating translation map settings
  const updateTranslationMapSetting = useUpdateTranslationMapSetting()

  // Update local content when dialog opens or content prop changes
  useEffect(() => {
    if (open) {
      setLocalContent(content)
      setHasChanges(false)
    }
  }, [open, content])

  const handleContentChange = (newContent: string) => {
    setLocalContent(newContent)
    setHasChanges(newContent !== content)
  }

  const handleSave = async () => {
    if (!translationMap || !currentSpecification) {
      toast.error('Missing required data for saving')
      return
    }

    setIsSaving(true)

    try {
      // Create updated specification
      const updatedSpecification = {
        ...currentSpecification,
        [field]: localContent,
      }

      // Create the full translation map object with updated specification
      const updatedTranslationMap = {
        ...translationMap,
        specification: updatedSpecification,
      }

      // Update the translation map settings
      await updateTranslationMapSetting.mutateAsync(updatedTranslationMap)

      // Call the parent onSave callback to update local state
      onSave(localContent)

      toast.success(`${field === 'summary' ? 'Summary' : 'Data Mapping'} saved successfully`)
      setHasChanges(false)
      onClose()
    } catch (error) {
      console.error('Failed to save specification:', error)
      toast.error(error)
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancel = () => {
    if (hasChanges) {
      // Could add confirmation dialog here if needed
      const confirmed = window.confirm('You have unsaved changes. Are you sure you want to cancel?')
      if (!confirmed) return
    }
    setLocalContent(content) // Reset to original content
    setHasChanges(false)
    onClose()
  }

  const handleClose = () => {
    if (hasChanges) {
      const confirmed = window.confirm('You have unsaved changes. Are you sure you want to close?')
      if (!confirmed) return
    }
    handleCancel()
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          width: '95vw',
          height: '95vh',
          maxWidth: 'none',
          maxHeight: 'none',
          margin: '2.5vh auto',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: 1,
          borderColor: 'divider',
          pb: 1,
        }}
      >
        <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{
            color: 'text.secondary',
            '&:hover': {
              color: 'text.primary',
              backgroundColor: 'action.hover',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          }}
        >
          <FileViewer
            content={localContent}
            contentType={contentType}
            onChange={handleContentChange}
            options={{
              readonly: disabled,
              autoPrettify: false,
              wordWrap: true,
              wordWrapColumn: 100,
              showValidations: true,
              heigth: 900,
            }}
            showOptions={false}
          />
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          borderTop: 1,
          borderColor: 'divider',
          p: 2,
          gap: 1,
        }}
      >
        <Button
          onClick={handleCancel}
          startIcon={<CancelIcon />}
          variant="outlined"
          color="inherit"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          startIcon={isSaving ? <CircularProgress size={16} /> : <SaveIcon />}
          variant="contained"
          color="primary"
          disabled={disabled || !hasChanges || isSaving}
        >
          {isSaving ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default SpecificationEditorDialog
